import { c as _c } from "react/compiler-runtime";
import type { Language, Locale, OnboardingRequirement, OnboardingSessionConfig } from '@onefootprint/request-types';
import { useState } from 'react';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import ConfirmContinueOnDesktop from './components/confirm-continue-on-desktop';
import NewTabTransfer from './components/new-tab-transfer';
import QrTransfer from './components/qr-transfer';
import SmsTransfer from './components/sms-transfer';
import type { TransferMode, TransferRequirements } from './types';
import hasAppclipSupport from './utils/has-appclip-support';

// When state is 'transfer', this component branches on transferMode.
// The other two states are user-driven, determined by user actions whilst in a transfer flow.
type TransferState = 'transfer' | 'confirm-desktop' | 'new-tab-for-liveness';
export type TransferProps = {
  authToken: string;
  device: DeviceInfo;
  config: OnboardingSessionConfig;
  isInIframe: boolean;
  l10n: {
    locale?: Locale;
    language: Language;
  };
  missingRequirements: TransferRequirements;
  missingHandoffRequirements: OnboardingRequirement[];
  sandboxIdDocOutcome?: string;
  transferMode: Exclude<TransferMode, 'skip'>;
  fromContinueOnMobile: boolean;
  onDone: () => void;
};
const Transfer = t0 => {
  const $ = _c(53);
  const {
    authToken,
    device,
    config,
    isInIframe,
    l10n,
    missingRequirements,
    missingHandoffRequirements,
    sandboxIdDocOutcome,
    transferMode,
    fromContinueOnMobile,
    onDone
  } = t0;
  const [state, setState] = useState("transfer");
  let t1;
  if ($[0] !== config.isAppClipEnabled || $[1] !== missingHandoffRequirements) {
    t1 = hasAppclipSupport(missingHandoffRequirements, config.isAppClipEnabled);
    $[0] = config.isAppClipEnabled;
    $[1] = missingHandoffRequirements;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const appclipSupported = t1;
  const needsNewTabForLiveness = !!missingRequirements.liveness && isInIframe && device.hasSupportForWebauthn;
  if (state === "new-tab-for-liveness") {
    let t2;
    if ($[3] !== authToken || $[4] !== config || $[5] !== device || $[6] !== fromContinueOnMobile || $[7] !== l10n || $[8] !== missingRequirements || $[9] !== onDone || $[10] !== sandboxIdDocOutcome) {
      t2 = <NewTabTransfer authToken={authToken} config={config} device={device} l10n={l10n} missingRequirements={missingRequirements} hasAppclipSupport={false} sandboxIdDocOutcome={sandboxIdDocOutcome} fromContinueOnMobile={fromContinueOnMobile} onDone={onDone} />;
      $[3] = authToken;
      $[4] = config;
      $[5] = device;
      $[6] = fromContinueOnMobile;
      $[7] = l10n;
      $[8] = missingRequirements;
      $[9] = onDone;
      $[10] = sandboxIdDocOutcome;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  if (state === "confirm-desktop") {
    let t2;
    if ($[12] !== needsNewTabForLiveness || $[13] !== onDone) {
      t2 = () => {
        if (needsNewTabForLiveness) {
          setState("new-tab-for-liveness");
        } else {
          onDone();
        }
      };
      $[12] = needsNewTabForLiveness;
      $[13] = onDone;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    let t3;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = () => setState("transfer");
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== t2) {
      t4 = <ConfirmContinueOnDesktop onConfirmDesktop={t2} onBackToMobile={t3} />;
      $[16] = t2;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    return t4;
  }
  let t2;
  if ($[18] !== missingRequirements.documents) {
    t2 = missingRequirements.documents.length > 0 && !missingRequirements.documents.some(_temp);
    $[18] = missingRequirements.documents;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  const shouldWarnOnDesktop = t2;
  let t3;
  if ($[20] !== needsNewTabForLiveness || $[21] !== onDone || $[22] !== shouldWarnOnDesktop) {
    t3 = () => {
      if (shouldWarnOnDesktop) {
        setState("confirm-desktop");
      } else {
        if (needsNewTabForLiveness) {
          setState("new-tab-for-liveness");
        } else {
          onDone();
        }
      }
    };
    $[20] = needsNewTabForLiveness;
    $[21] = onDone;
    $[22] = shouldWarnOnDesktop;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  const handleContinueOnDesktop = t3;
  if (transferMode === "qr") {
    let t4;
    if ($[24] !== authToken || $[25] !== config || $[26] !== device || $[27] !== fromContinueOnMobile || $[28] !== handleContinueOnDesktop || $[29] !== l10n || $[30] !== missingRequirements || $[31] !== onDone || $[32] !== sandboxIdDocOutcome) {
      t4 = <QrTransfer authToken={authToken} config={config} device={device} l10n={l10n} missingRequirements={missingRequirements} sandboxIdDocOutcome={sandboxIdDocOutcome} onContinueOnDesktop={handleContinueOnDesktop} fromContinueOnMobile={fromContinueOnMobile} onDone={onDone} />;
      $[24] = authToken;
      $[25] = config;
      $[26] = device;
      $[27] = fromContinueOnMobile;
      $[28] = handleContinueOnDesktop;
      $[29] = l10n;
      $[30] = missingRequirements;
      $[31] = onDone;
      $[32] = sandboxIdDocOutcome;
      $[33] = t4;
    } else {
      t4 = $[33];
    }
    return t4;
  }
  if (transferMode === "sms") {
    let t4;
    if ($[34] !== authToken || $[35] !== config || $[36] !== device || $[37] !== fromContinueOnMobile || $[38] !== l10n || $[39] !== missingRequirements || $[40] !== onDone || $[41] !== sandboxIdDocOutcome) {
      t4 = <SmsTransfer authToken={authToken} config={config} device={device} l10n={l10n} missingRequirements={missingRequirements} sandboxIdDocOutcome={sandboxIdDocOutcome} fromContinueOnMobile={fromContinueOnMobile} onDone={onDone} />;
      $[34] = authToken;
      $[35] = config;
      $[36] = device;
      $[37] = fromContinueOnMobile;
      $[38] = l10n;
      $[39] = missingRequirements;
      $[40] = onDone;
      $[41] = sandboxIdDocOutcome;
      $[42] = t4;
    } else {
      t4 = $[42];
    }
    return t4;
  }
  if (transferMode === "new-tab") {
    let t4;
    if ($[43] !== appclipSupported || $[44] !== authToken || $[45] !== config || $[46] !== device || $[47] !== fromContinueOnMobile || $[48] !== l10n || $[49] !== missingRequirements || $[50] !== onDone || $[51] !== sandboxIdDocOutcome) {
      t4 = <NewTabTransfer authToken={authToken} config={config} device={device} l10n={l10n} missingRequirements={missingRequirements} hasAppclipSupport={appclipSupported} sandboxIdDocOutcome={sandboxIdDocOutcome} fromContinueOnMobile={fromContinueOnMobile} onDone={onDone} />;
      $[43] = appclipSupported;
      $[44] = authToken;
      $[45] = config;
      $[46] = device;
      $[47] = fromContinueOnMobile;
      $[48] = l10n;
      $[49] = missingRequirements;
      $[50] = onDone;
      $[51] = sandboxIdDocOutcome;
      $[52] = t4;
    } else {
      t4 = $[52];
    }
    return t4;
  }
  return null;
};
export default Transfer;
function _temp(doc) {
  return doc.uploadSettings === "prefer_upload";
}